/**
 * Swiping Module
 * Handles user swiping functionality and match detection
 */
import { SwipingAPI } from '../services/api.js';
import { AppState, updateState, normalizeImageUrl } from '../config.js';
import { showToast, Navigation, Utils } from '../utils/ui.js';
import { t } from '../utils/i18n.js';
import { renderGamePicker } from './games.js';
import { haptic } from '../utils/haptics.js';

let currentImageIndex = 0;
let lastTapTime = 0;

/**
 * Load users available for swiping at an event
 */
export async function loadSwipeUsers(eventId) {
    try {
        console.log('Loading swipe users for event:', eventId);
        document.getElementById('swipe-container').innerHTML = '';

        const response = await SwipingAPI.getSwipeUsers(eventId);
        console.log('Swipe users response:', response);

        updateState({
            swipeUsers: response.users,
            currentSwipeIndex: 0
        });

        const eventInfo = document.getElementById('event-info');
        if (response.event) {
            eventInfo.textContent = t('swiping.swipingAt', { name: response.event.name });
        }

        updateSwipingShareButton();

        if (AppState.swipeUsers.length === 0) {
            document.getElementById('swipe-container').innerHTML = `
                <div class="text-center py-12">
                    <i class="fas fa-heart-broken text-4xl text-muted-foreground mb-4 float-animation pop-in"></i>
                    <h3 class="text-lg font-medium text-foreground mb-2">${Utils.escapeHtml(t('swiping.noMorePeople'))}</h3>
                    <p class="text-muted-foreground">${Utils.escapeHtml(t('swiping.checkBackOrTry'))}</p>
                </div>
            `;
        } else {
            console.log('Rendering swipe card for user:', AppState.swipeUsers[0]);
            renderSwipeCard();
        }

    } catch (error) {
        console.error('Failed to load swipe users:', error);
        showToast(t('swiping.failedLoadSwipe'), 'error');

        // Show error details in the UI
        document.getElementById('swipe-container').innerHTML = `
            <div class="text-center py-12">
                <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
                <h3 class="text-lg font-medium text-foreground mb-2">${Utils.escapeHtml(t('swiping.failedLoadUsers'))}</h3>
                <p class="text-muted-foreground mb-4">${Utils.escapeHtml(error.message || '')}</p>
                <button onclick="loadSwipeUsers(${parseInt(eventId)})" class="shadcn-button shadcn-button-primary btn-press breathe-glow">
                    <i class="fas fa-refresh mr-2"></i>${Utils.escapeHtml(t('swiping.tryAgain'))}
                </button>
            </div>
        `;
    }
}

/**
 * Render the current swipe card with card stack effect
 */
function renderSwipeCard() {
    if (AppState.currentSwipeIndex >= AppState.swipeUsers.length) {
        document.getElementById('swipe-container').innerHTML = `
            <div class="text-center py-12">
                <i class="fas fa-check-circle text-4xl text-green-500 mb-4 heart-burst pop-in"></i>
                <h3 class="text-lg font-medium text-foreground mb-2">${Utils.escapeHtml(t('swiping.allCaughtUp'))}</h3>
                <p class="text-muted-foreground">${Utils.escapeHtml(t('swiping.seenEveryone'))}</p>
                <button onclick="loadSwipeUsers(${parseInt(AppState.currentEvent.id)})" class="mt-4 shadcn-button shadcn-button-primary btn-press breathe-glow">
                    <i class="fas fa-refresh mr-2"></i>${Utils.escapeHtml(t('home.refresh'))}
                </button>
            </div>
        `;
        return;
    }

    // Show onboarding card for first-time swipers
    if (AppState.currentSwipeIndex === 0 && !localStorage.getItem('ss_has_swiped')) {
        const behindUser = AppState.swipeUsers[0];
        const behindImg = normalizeImageUrl((behindUser.profile_images || [])[0]) || 'https://via.placeholder.com/300x400?text=No+Photo';
        document.getElementById('swipe-container').innerHTML = `
            <div class="swipe-card swipe-card-behind-1 h-96">
                <div class="relative h-full">
                    <img src="${behindImg}" alt="" class="w-full h-full object-cover rounded-xl">
                </div>
            </div>
            <div class="swipe-card h-96 onboarding-swipe-card" id="current-swipe-card">
                <div class="relative h-full rounded-xl overflow-hidden" style="background: linear-gradient(135deg, #4B0082, #FF00FF);">
                    <!-- LIKE / NOPE stamps -->
                    <div class="swipe-stamp swipe-stamp-like" id="stamp-like">LIKE</div>
                    <div class="swipe-stamp swipe-stamp-nope" id="stamp-nope">NOPE</div>

                    <div class="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
                        <div class="text-5xl mb-4" style="animation: matchHeartAppear 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.2s both;">👋</div>
                        <h3 class="text-2xl font-bold text-white mb-3">${Utils.escapeHtml(t('swiping.onboardingTitle'))}</h3>
                        <p class="text-white/70 text-sm">${Utils.escapeHtml(t('swiping.onboardingHint'))}</p>
                        <div class="flex items-center gap-8 mt-8">
                            <div class="flex items-center gap-2 text-red-300"><i class="fas fa-arrow-left"></i> NOPE</div>
                            <div class="flex items-center gap-2 text-green-300">LIKE <i class="fas fa-arrow-right"></i></div>
                        </div>
                    </div>
                </div>
            </div>
        `;
        const onboardingDismiss = () => {
            localStorage.setItem('ss_has_swiped', '1');
            setTimeout(() => renderSwipeCard(), 300);
        };
        setupSwipeGestures({ onSwipeComplete: onboardingDismiss });
        setupOnboardingDismiss();
        return;
    }

    const user = AppState.swipeUsers[AppState.currentSwipeIndex];
    const profileImages = user.profile_images || [];
    const mainImage = normalizeImageUrl(profileImages[0]) || 'https://via.placeholder.com/300x400?text=No+Photo';

    // Build behind cards for stack effect
    let behindCards = '';
    for (let i = 1; i <= 2; i++) {
        const behindIdx = AppState.currentSwipeIndex + i;
        if (behindIdx < AppState.swipeUsers.length) {
            const behindUser = AppState.swipeUsers[behindIdx];
            const behindImg = normalizeImageUrl((behindUser.profile_images || [])[0]) || 'https://via.placeholder.com/300x400?text=No+Photo';
            behindCards += `
                <div class="swipe-card swipe-card-behind-${i} h-96">
                    <div class="relative h-full">
                        <img src="${behindImg}" alt="" class="w-full h-full object-cover rounded-xl">
                    </div>
                </div>
            `;
        }
    }

    const cardHTML = `
        ${behindCards}
        <div class="swipe-card h-96" id="current-swipe-card">
            <div class="relative h-full">
                <img src="${mainImage}" alt="${Utils.escapeHtml(user.name)}" class="w-full h-full object-cover rounded-xl">

                <!-- LIKE / NOPE stamps -->
                <div class="swipe-stamp swipe-stamp-like" id="stamp-like">LIKE</div>
                <div class="swipe-stamp swipe-stamp-nope" id="stamp-nope">NOPE</div>

                <!-- Image indicators if multiple photos -->
                ${profileImages.length > 1 ? `
                    <div class="absolute top-3 left-3 right-3 flex gap-1">
                        ${profileImages.map((_, index) => `
                            <div class="image-indicator-bar h-[3px] rounded-full flex-1 ${index === 0 ? 'bg-white' : 'bg-white/30'}"></div>
                        `).join('')}
                    </div>
                ` : ''}

                <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 pt-20 rounded-b-xl">
                    <div class="flex items-baseline gap-3 mb-2">
                        <h3 class="text-2xl font-bold text-white">${Utils.escapeHtml(user.name)}</h3>
                        ${user.age ? `<span class="text-xl text-white/90">${Utils.escapeHtml(String(user.age))}</span>` : ''}
                    </div>
                    ${user.bio ? `<p class="text-gray-200 text-sm line-clamp-2">${Utils.escapeHtml(user.bio)}</p>` : ''}
                </div>
            </div>
        </div>
    `;

    document.getElementById('swipe-container').innerHTML = cardHTML;

    // Reset image index for new card
    currentImageIndex = 0;

    // Add swipe gesture support
    setupSwipeGestures();
}

/**
 * Setup swipe gesture handling for cards
 */
function setupSwipeGestures({ onSwipeComplete } = {}) {
    const card = document.getElementById('current-swipe-card');
    if (!card) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isDragging = false;
    let directionLocked = false; // true once we know it's a horizontal swipe

    card.addEventListener('mousedown', startDrag);
    card.addEventListener('touchstart', startDrag, { passive: true });

    function startDrag(e) {
        isDragging = true;
        directionLocked = false;
        const point = e.type === 'mousedown' ? e : e.touches[0];
        startX = point.clientX;
        startY = point.clientY;
        currentX = startX;

        document.addEventListener('mousemove', drag);
        document.addEventListener('touchmove', drag, { passive: false });
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);
    }

    function drag(e) {
        if (!isDragging) return;

        const point = e.type === 'mousemove' ? e : e.touches[0];
        const dx = point.clientX - startX;
        const dy = point.clientY - startY;

        // On the first significant move, decide: horizontal swipe or vertical scroll
        if (!directionLocked) {
            if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return; // too small to decide
            if (Math.abs(dy) > Math.abs(dx)) {
                // Vertical movement — let the browser scroll, cancel our drag
                isDragging = false;
                cleanup();
                return;
            }
            // Horizontal — lock in swipe mode
            directionLocked = true;
            card.classList.add('dragging');
        }

        // Prevent page scroll while swiping the card
        e.preventDefault();

        currentX = point.clientX;
        const deltaX = currentX - startX;
        const rotation = deltaX * 0.1;

        card.style.transform = `translateX(${deltaX}px) rotate(${rotation}deg)`;
        card.style.opacity = Math.max(0.3, 1 - Math.abs(deltaX) / 300);

        // Show LIKE/NOPE stamps based on drag direction
        const stampLike = document.getElementById('stamp-like');
        const stampNope = document.getElementById('stamp-nope');
        const progress = Math.min(1, Math.abs(deltaX) / 120);

        if (stampLike && stampNope) {
            if (deltaX > 20) {
                stampLike.style.opacity = progress;
                stampLike.style.transform = `rotate(-20deg) scale(${0.8 + progress * 0.3})`;
                stampNope.style.opacity = 0;
                card.classList.add('glow-right');
                card.classList.remove('glow-left');
            } else if (deltaX < -20) {
                stampNope.style.opacity = progress;
                stampNope.style.transform = `rotate(20deg) scale(${0.8 + progress * 0.3})`;
                stampLike.style.opacity = 0;
                card.classList.add('glow-left');
                card.classList.remove('glow-right');
            } else {
                stampLike.style.opacity = 0;
                stampNope.style.opacity = 0;
                card.classList.remove('glow-right', 'glow-left');
            }
        }
    }

    function endDrag() {
        if (!isDragging && !directionLocked) {
            cleanup();
            return;
        }

        isDragging = false;
        card.classList.remove('dragging');

        const deltaX = currentX - startX;
        const threshold = 80;

        // Reset stamps and glow
        const stampLike = document.getElementById('stamp-like');
        const stampNope = document.getElementById('stamp-nope');
        if (stampLike) { stampLike.style.opacity = 0; stampLike.style.transform = ''; }
        if (stampNope) { stampNope.style.opacity = 0; stampNope.style.transform = ''; }
        card.classList.remove('glow-right', 'glow-left');

        if (directionLocked && Math.abs(deltaX) > threshold) {
            // Always: right = like, left = dislike (physical direction, independent of RTL)
            const direction = deltaX > 0 ? 'like' : 'dislike';
            if (onSwipeComplete) {
                card.classList.add(direction === 'like' ? 'swiped-right' : 'swiped-left');
                onSwipeComplete(direction);
            } else {
                swipeUser(direction);
            }
        } else {
            // Snap back
            card.style.transform = '';
            card.style.opacity = '';

            // Detect tap (no significant drag) for image browsing
            if (!directionLocked && Math.abs(deltaX) < 5) {
                handleImageTap(card, startX);
            }
        }

        cleanup();
    }

    function cleanup() {
        document.removeEventListener('mousemove', drag);
        document.removeEventListener('touchmove', drag);
        document.removeEventListener('mouseup', endDrag);
        document.removeEventListener('touchend', endDrag);
    }
}

/**
 * Handle tap on card to browse multiple profile images
 */
function handleImageTap(card, tapX) {
    const now = Date.now();
    if (now - lastTapTime < 300) return;
    lastTapTime = now;

    if (card.classList.contains('onboarding-swipe-card')) return;
    if (AppState.currentSwipeIndex >= AppState.swipeUsers.length) return;

    const user = AppState.swipeUsers[AppState.currentSwipeIndex];
    const images = user.profile_images || [];
    if (images.length <= 1) return;

    const cardRect = card.getBoundingClientRect();
    const tapRelativeX = tapX - cardRect.left;
    const cardMidpoint = cardRect.width / 2;

    if (tapRelativeX > cardMidpoint) {
        currentImageIndex = Math.min(currentImageIndex + 1, images.length - 1);
    } else {
        currentImageIndex = Math.max(currentImageIndex - 1, 0);
    }

    // Update displayed image
    const img = card.querySelector('img');
    if (img) {
        img.src = normalizeImageUrl(images[currentImageIndex]) || img.src;
    }

    // Update indicator bars
    card.querySelectorAll('.image-indicator-bar').forEach((bar, index) => {
        bar.classList.remove('bg-white', 'bg-white/30');
        bar.classList.add(index === currentImageIndex ? 'bg-white' : 'bg-white/30');
    });
}

/**
 * Perform swipe action (like or dislike)
 */
let isSwiping = false;

export async function swipeUser(swipeType) {
    if (isSwiping) return;
    if (AppState.currentSwipeIndex >= AppState.swipeUsers.length) return;

    isSwiping = true;

    // Disable buttons visually during swipe
    const passBtn = document.getElementById('pass-btn');
    const likeBtn = document.getElementById('like-btn');
    if (passBtn) passBtn.disabled = true;
    if (likeBtn) likeBtn.disabled = true;

    const user = AppState.swipeUsers[AppState.currentSwipeIndex];
    const card = document.getElementById('current-swipe-card');

    // Animate card out
    if (card) {
        card.classList.remove('glow-right', 'glow-left');
        card.classList.add(swipeType === 'like' ? 'swiped-right' : 'swiped-left');
    }
    haptic('swipe');

    try {
        const response = await SwipingAPI.swipe(AppState.currentEvent.id, user.id, swipeType);

        if (response.match) {
            showMatchCelebration(user, response.match);
        }

    } catch (error) {
        console.error('Swipe failed:', error);
        showToast(t('swiping.swipeFailed'), 'error');
    }

    // Move to next user after animation
    setTimeout(() => {
        updateState({ currentSwipeIndex: AppState.currentSwipeIndex + 1 });
        renderSwipeCard();
        isSwiping = false;
        if (passBtn) passBtn.disabled = false;
        if (likeBtn) likeBtn.disabled = false;
    }, 300);
}

/**
 * Generate CSS-only confetti particles
 */
function generateConfetti(count = 20) {
    const colors = ['#FF00FF', '#00FFFF', '#CC00CC', '#9900FF', '#22c55e', '#facc15', '#FF66FF'];
    const shapes = ['circle', 'square'];
    let html = '';
    for (let i = 0; i < count; i++) {
        const color = colors[i % colors.length];
        const shape = shapes[i % shapes.length];
        const left = Math.random() * 100;
        const delay = Math.random() * 2;
        const size = 6 + Math.random() * 8;
        const borderRadius = shape === 'circle' ? '50%' : '2px';
        html += `<div class="confetti-piece" style="left:${left}%;background:${color};width:${size}px;height:${size}px;border-radius:${borderRadius};animation-delay:${delay}s;"></div>`;
    }
    return html;
}

/**
 * Show match celebration with confetti, animated avatars, and heart
 */
function showMatchCelebration(user, match) {
    haptic('celebration');

    const myImage = normalizeImageUrl((AppState.currentUser?.profile_images || [])[0]) || '';
    const theirImage = normalizeImageUrl((user.profile_images || [])[0]) || '';

    const popup = document.createElement('div');
    popup.className = 'fixed inset-0 match-celebration flex flex-col items-center justify-center z-50';
    popup.innerHTML = `
        <!-- Confetti -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
            ${generateConfetti(35)}
        </div>

        <!-- Content -->
        <div class="relative z-10 text-center px-6">
            <div class="match-celebration-title mb-6">${Utils.escapeHtml(t('swiping.itsAMatch'))}</div>

            <!-- Avatars -->
            <div class="flex items-center justify-center gap-4 mb-6">
                <div class="w-24 h-24 rounded-full border-4 border-white/50 overflow-hidden bg-white/20" style="animation: matchAvatarLeft 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.2s both;">
                    ${myImage ? `<img src="${myImage}" class="w-full h-full object-cover" alt="">` : '<div class="w-full h-full flex items-center justify-center text-white text-2xl"><i class="fas fa-user"></i></div>'}
                </div>
                <div style="animation: matchHeartAppear 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.5s both;">
                    <i class="fas fa-heart text-3xl text-white" style="filter: drop-shadow(0 0 12px rgba(255,255,255,0.6));"></i>
                </div>
                <div class="w-24 h-24 rounded-full border-4 border-white/50 overflow-hidden bg-white/20" style="animation: matchAvatarRight 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.2s both;">
                    ${theirImage ? `<img src="${theirImage}" class="w-full h-full object-cover" alt="">` : '<div class="w-full h-full flex items-center justify-center text-white text-2xl"><i class="fas fa-user"></i></div>'}
                </div>
            </div>

            <p class="text-white/90 text-lg mb-8">${Utils.escapeHtml(t('swiping.youAndLiked', { name: user.name }))}</p>

            <div class="flex gap-4 max-w-sm mx-auto">
                <button onclick="this.closest('.fixed').remove()"
                        class="flex-1 py-3 rounded-xl border-2 border-white/40 text-white font-semibold hover:bg-white/10 transition-all btn-press">
                    ${Utils.escapeHtml(t('swiping.keepSwiping'))}
                </button>
                <button onclick="window._openMatchGame(${parseInt(match.id)}); this.closest('.fixed').remove()"
                        class="flex-1 py-3 rounded-xl bg-white text-fuchsia-600 font-semibold hover:bg-white/90 transition-all btn-press breathe-glow" style="box-shadow: 0 4px 14px rgba(255,255,255,0.3);">
                    <i class="fas fa-gamepad mr-1"></i>${Utils.escapeHtml(t('swiping.breakTheIce'))}
                </button>
            </div>
            <button onclick="openChat(${parseInt(match.id)}); this.closest('.fixed').remove()"
                    class="mt-3 text-white/60 text-sm hover:text-white/90 transition-colors">
                ${Utils.escapeHtml(t('swiping.orJustSayHi'))} <i class="fas fa-arrow-right text-xs ml-1"></i>
            </button>
        </div>
    `;

    document.body.appendChild(popup);

    // Auto remove after 8 seconds
    setTimeout(() => {
        if (popup.parentNode) popup.remove();
    }, 8000);
}

/**
 * Setup swiping event listeners
 */
let swipingListenersInitialized = false;

export function setupSwipingEventListeners() {
    if (swipingListenersInitialized) return;
    swipingListenersInitialized = true;

    const passBtn = document.getElementById('pass-btn');
    const likeBtn = document.getElementById('like-btn');

    if (passBtn) passBtn.addEventListener('click', () => swipeUser('dislike'));
    if (likeBtn) likeBtn.addEventListener('click', () => swipeUser('like'));

    // Listen for real-time match notifications via Socket.io
    window.addEventListener('socket:new_match', (e) => {
        const { matchId, customer } = e.detail;
        // Show match popup for incoming match (when the OTHER person swipes and creates the match)
        if (customer) {
            showMatchCelebration(customer, { id: matchId });
        }
    });
}

/**
 * Share the current event's invite link (delegates to home.js shareInviteLink)
 */
async function shareCurrentEvent() {
    const event = AppState.currentEvent;
    if (!event || !event.invite_code) return;

    // Reuse the shareInviteLink function from home module
    const { shareInviteLink } = await import('./home.js');
    shareInviteLink(event.invite_code, event.name, null);
}

/**
 * Update visibility of the swiping share button based on current event
 */
export function updateSwipingShareButton() {
    const btn = document.getElementById('swiping-share-btn');
    if (!btn) return;
    if (AppState.currentEvent && AppState.currentEvent.invite_code) {
        btn.classList.remove('hidden');
    } else {
        btn.classList.add('hidden');
    }
}

/**
 * Dismiss onboarding card on any swipe/click and show first real card
 */
function setupOnboardingDismiss() {
    const dismiss = () => {
        localStorage.setItem('ss_has_swiped', '1');
        const card = document.getElementById('current-swipe-card');
        if (card) card.classList.add('swiped-right');
        setTimeout(() => renderSwipeCard(), 300);
    };

    // Dismiss on button press (drag gestures handled by setupSwipeGestures)
    const passBtn = document.getElementById('pass-btn');
    const likeBtn = document.getElementById('like-btn');
    if (passBtn) passBtn.addEventListener('click', dismiss, { once: true });
    if (likeBtn) likeBtn.addEventListener('click', dismiss, { once: true });
}

/**
 * Open game picker from match celebration popup
 */
function openMatchGame(matchId) {
    // Navigate to chat first (sets up AppState.currentMatch), then open game picker
    if (window.openChat) window.openChat(matchId);
    setTimeout(() => renderGamePicker(matchId), 400);
}

// Make functions globally available for HTML onclick handlers
window.swipeUser = swipeUser;
window.loadSwipeUsers = loadSwipeUsers;
window.shareCurrentEvent = shareCurrentEvent;
window._openMatchGame = openMatchGame;
